<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { getContext, type Component, type Snippet } from "svelte";
	import { readable, writable, type Readable, type Writable } from "svelte/store";
	import { PageContainerSchema, type AssetRef, type Suite } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import AppSwitcherLayout from "#lib/components/flow/viewer/AppSwitcherLayout.svelte";
	import DesktopAppSwitcherLayout from "#lib/components/flow/viewer/desktop/AppSwitcherLayout.svelte";
	import Blank from "#lib/components/flow/viewer/layout/Blank.svelte";
	import type { AppSwitcherLayoutProps, ContainerProps } from "#lib/components/flow/viewer/types.js";
	import { resolveBackgroundPaint } from "#lib/components/flow/viewer/utils.js";
	import { APP_ANCHOR_NAME, type BundleNode } from "#lib/dag.js";
	import { isFeatureEnabled } from "#lib/featureFlags.js";
	import type { JwtPayload } from "#lib/jwt.js";
	import type { Client } from "@optimizely/optimizely-sdk";

	const flags: Client = getContext("flags");
	const claims: Writable<JwtPayload> = getContext("claims");
	const suite: Readable<Suite | undefined> = getContext<Readable<Suite | undefined> | undefined>("suite") ?? readable(undefined);
	const isDesktopUserAgent: Readable<boolean | undefined> = getContext<Readable<boolean | undefined> | undefined>("isDesktop") ?? readable(false);

	const { dag, asset, mode = Mode.LIVE, readOnly = false, interpolate = false, selected = false, isDesktop = false, metadata, themeStyle, isLightBackground, onExecute, onCancel, modal = false, showDisabled = false, onShow, onMore, onMenu, onShare, onEdit, apps: nestedApps, app: appAccessory, ...other }: ContainerProps = $props();

	const key = $derived(asset?.key());
	const container = $derived(asset?.asPageContainer() ?? writable(create(PageContainerSchema)));
	const appModules = $derived(dag.findModulesFromAssetKey(key, APP_ANCHOR_NAME));
	const accessory = $derived(getAccessory(nestedApps));
	const readOnlyClass = $derived(readOnly ? "disabled:opacity-100" : "");
	const borderClass = $derived(isLightBackground ? "border border-gray-300" : "border border-[var(--background-color)]");
	const outlineClass = $derived(mode === Mode.EDITING && selected ? "outline outline-offset-4 outline-transparent hover:outline-primary-600" : "");
	const backgroundPaint = $derived(resolveBackgroundPaint({ mode, modal, isDesktopUserAgent: $isDesktopUserAgent, desktopViewEnabled: $suite?.desktopViewEnabled }));
	const useDesktopLayout = $derived(isDesktop && isFeatureEnabled(flags, "desktop_landing_pages", $claims));
	const Switcher = $derived(getAppSwitcherLayout($container.layoutName, useDesktopLayout));

	function getAppSwitcherLayout(name: string | undefined, isDesktop?: boolean): Component<AppSwitcherLayoutProps> {
		if (!name) {
			return isDesktop ? DesktopAppSwitcherLayout : AppSwitcherLayout;
		}
		return isDesktop ? DesktopAppSwitcherLayout : AppSwitcherLayout;
	}

	function getAccessory(app?: Snippet): Record<string, Snippet | undefined> {
		return {
			[APP_ANCHOR_NAME]: app,
		};
	}

	function isModuleEnabled(node: BundleNode<AssetRef>): boolean {
		return !node?.ref.disabled || showDisabled;
	}
</script>

<Blank {...other} {dag} {key} container={$container} {accessory} {mode} {readOnly} {modal} {backgroundPaint} {onShow} {onMore} {onMenu} {onShare}>
	{#snippet apps()}
		{#if appModules.filter(isModuleEnabled).length > 0}
			<Switcher apps={appModules.filter(isModuleEnabled)} layoutName={$container.layoutName} {themeStyle} classes={{ borderClass, readOnlyClass, outlineClass }} {isLightBackground} accessory={appAccessory} {metadata} {mode} {readOnly} {interpolate} {onShow} {onMore} {onExecute} {onCancel} {onEdit} />
		{/if}
	{/snippet}
</Blank>
